import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';

import { chartSeriesFor } from '../../../theme/muiTheme';
import { formatNumber } from '../../../shared/utils/number';

function YearlyTooltip({ active, label, payload }) {
  if (!active || !payload?.[0]?.payload) return null;
  const row = payload[0].payload;
  const entries = [
    ['Scope 1', row.scope1],
    ['Scope 2', row.scope2],
    ['Scope 3', row.scope3],
    ['Total', row.total],
  ];
  return (
    <Paper elevation={3} sx={{ minWidth: 190, p: 1.5 }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, mb: 1 }}>
        {label}
      </Typography>
      <Stack spacing={0.75}>
        {entries.map(([name, value]) => (
          <Stack key={name} direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="caption" color={name === 'Total' ? 'text.primary' : 'text.secondary'} sx={{ fontWeight: name === 'Total' ? 800 : 500 }}>
              {name}
            </Typography>
            <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
              {formatNumber(value, { min: 2, max: 2 })} tCO₂e
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

const formatAxis = (value) => (
  Math.abs(value) >= 1000
    ? `${formatNumber(value / 1000, { min: 0, max: 0 })}k`
    : formatNumber(value, { max: 0 })
);

const formatBarLabel = (value) => {
  if (value == null) return '';
  if (Math.abs(value) >= 1000) return `${formatNumber(value / 1000, { min: 1, max: 1 })}k`;
  return formatNumber(value, { min: 0, max: 1 });
};

export default function YearlyInventoryChart({ data }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const colors = chartSeriesFor(theme.palette.mode === 'dark');

  return (
    <Box sx={{ height: { xs: 260, md: 300 }, minWidth: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 24, right: compact ? 0 : 10, left: compact ? -18 : 0, bottom: 0 }}
          barCategoryGap="32%"
        >
          <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="year"
            axisLine={{ stroke: theme.palette.divider }}
            tick={{ fill: theme.palette.text.secondary, fontSize: 13 }}
            tickLine={false}
          />
          <YAxis
            axisLine={false}
            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
            tickFormatter={formatAxis}
            tickLine={false}
            width={compact ? 42 : 52}
          />
          <Tooltip
            cursor={{ fill: theme.palette.action.hover }}
            content={<YearlyTooltip />}
          />
          <Bar
            dataKey="total"
            name={t('dashboard.scope.total')}
            fill={colors.TOTAL}
            maxBarSize={64}
            radius={[5, 5, 0, 0]}
          >
            <LabelList
              dataKey="total"
              position="top"
              formatter={formatBarLabel}
              style={{ fill: theme.palette.text.secondary, fontSize: compact ? 11 : 12, fontWeight: 700 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
